import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Row } from '../../styles';
import VoteCard from './VoteCard';
import { getSurvey, getUserSurveyGroup, getUserSurvey, submitSurvey, Survey, SurveyGroup, SurveyItem, UserVote } from '../../apiService';
import SurveyHeader from './SurveyHeader';
import VoteSummary from './VoteSummary';
import AlertService from '../../AlertService';
import { __t } from '../../i18n';
import { useUser } from '../../CacheProvider';
import Dialog from '../../Dialog';
import { paperBg } from '../../styles/Paper';
import { globalPadding } from '../../Layout/Layout';

const VotesContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.rgba(theme.palette.background[paperBg.main][0], .5),
}));

export type VotesStateType = {
  [key: string]: {
    point: number;
    img?: string | null;
  }
}

const getCurrentSurveyIndex = (surveys?: Survey[], contentId?: string) => {
  if (!surveys || !contentId) return 0;
  const index = surveys.findIndex(({ reference_id }) => reference_id === contentId);
  if (index >= 0) { return index; }
  const now = new Date();
  const lastActive = [...surveys].reverse().find(({ user_participated_points, policy }) => {
    return (user_participated_points && user_participated_points > 0) || new Date(policy.to) > now;
  });
  const lastActiveSurveyIndex = surveys.findIndex((x) => x.id === lastActive?.id);
  return lastActiveSurveyIndex >= 0 ? lastActiveSurveyIndex : 0;
};

const getLimits = (survey: Survey, votes: VotesStateType) => {
  const totalPoints = votes ? Object.values(votes).map(({ point }) => point).reduce((t, p) => t + p, 0) : 0;
  const totalItems = votes ? Object.values(votes).filter(({ point }) => point > 0).length : 0;
  if (totalPoints >= survey?.policy?.max_points) return 'vote.total_vote_restriction';
  if (totalItems >= survey?.policy?.max_items) return 'vote.item_restriction';
  return '';
};

const initailVotesState = (surveyItems?: SurveyItem[]): VotesStateType => {
  const state = {};
  if (!surveyItems) return state;
  surveyItems.forEach(({ id, image_path, point }) => {
    state[id] = {
      point: point || 0,
      img: image_path,
    };
  });
  return state;
};

const getSurveyDisabledState = (survey?: Survey) => {
  if (!survey) return false;

  const { policy } = survey;
  const now = new Date();
  const expiryDate = new Date(policy.to);
  return expiryDate <= now;
};

interface propsType {
  surveyGroup: SurveyGroup;
  contentId: string;
}

export default function ContentSurvey({ surveyGroup, contentId }: propsType) {
  const { surveys, reference_id: groupId } = surveyGroup;
  const [activeSurvey, setActiveSurvey] = useState<number>(getCurrentSurveyIndex(surveys, contentId));
  const surveyReferenceId = surveys?.[activeSurvey].reference_id;
  const [survey, setSurvey] = useState<Survey>();
  const [votes, setVotes] = useState(initailVotesState(survey?.items));
  const [userParticipatedPoints, setUserParticipatedPoints] = useState<boolean>();
  const [showSummary, setShowSummary] = useState(false);
  const { authorized } = useUser();
  const [groupRemainingPoints, setGroupRemainingPoints] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const disabled = getSurveyDisabledState(survey) || (Number(userParticipatedPoints) > 0);
  const { loaded } = useUser();

  const handleVoteChange = (v: UserVote) => {
    if (!survey?.policy || !votes) return;
    const newVotes = { ...votes };
    newVotes[v.survey_item_id].point = v.vote_points;
    setVotes(newVotes);
  };

  const handleSurveyChange = (index: number) => {
    setActiveSurvey(index);
  };

  const handleClear = () => {
    setShowSummary(false);
    setVotes(initailVotesState(survey?.items));
  };

  const handleSubmit = () => {
    setShowSummary(false);
    const surveyId = surveys?.[activeSurvey]?.id;
    if (!surveyId) return;

    const userVote: UserVote[] = Object.entries(votes)
      .filter(([_, { point }]) => point > 0)
      .map(([survey_item_id, { point: vote_points }]) => ({ survey_item_id, vote_points }));

    if (!survey || !userVote?.length) return;

    const minItemsVoted = userVote.length >= survey.policy.min_items;
    if (!minItemsVoted) {
      AlertService.error(__t('vote.min_item_restriction', { minItem: survey.policy.min_items || 0 }));
      return;
    }

    submitSurvey({
      surveyId,
      group_id: surveyGroup.id,
      user_vote: userVote,
    }).then(({ meta: { display_message } }) => {
      setUserParticipatedPoints(true);
      display_message && AlertService.success(display_message);
    });
  };

  useEffect(() => {
    loaded && surveyReferenceId && getSurvey({ referenceId: surveyReferenceId })
      .then(({ data }) => {
        setSurvey(data);
      });
  }, [surveyReferenceId, loaded]);

  useEffect(() => {
    setVotes(initailVotesState(survey?.items));
  }, [survey?.items]);

  useEffect(() => {
    setActiveSurvey(getCurrentSurveyIndex(surveys, contentId));
  }, [contentId, surveys]);

  useEffect(() => {
    if (!authorized) return;

    if (surveyReferenceId) {
      setLoading(true);
      getUserSurvey({ reference: surveyReferenceId })
        .then(({ data: { items, user_participated_points } }) => {
          setLoading(false);
          setUserParticipatedPoints(!!user_participated_points);

          const newItems = survey?.items?.length ? survey?.items.map((survey) => {
            const s = items.find((item) => item.id === survey.id);
            return { ...survey, point: s?.vote_points || 0 };
          }) : [];

          if (newItems?.length) {
            const newVotes = initailVotesState(newItems);
            setVotes(newVotes);
          }
        })
        .catch(() => {
          setLoading(false);
        });
    }

    if (survey?.policy?.max_points) {
      setLoading(true);
      getUserSurveyGroup({ reference: groupId })
        .then(({ data: { current_user_remain_points } }) => {
          setLoading(false);
          setGroupRemainingPoints(current_user_remain_points);
          // setGroupSurveys(surveys);
        }).catch(() => {
          setLoading(false);
        });
    }
  }, [authorized, groupId, survey?.items, survey?.policy?.max_points, surveyReferenceId]);

  return (
    <div>
      <SurveyHeader
        remainingPoints={{
          group: groupRemainingPoints,
        }}
        surveyGroup={surveyGroup}
        onChangeSurvey={handleSurveyChange}
        onSubmit={() => setShowSummary(true)}
        activeSurvey={activeSurvey}
        disabled={disabled}
        userParticipatedPoints={userParticipatedPoints}
        votes={votes}
      />
      {survey && (
        <VotesContainer
          py={{ xs: 3, md: 7 }}
          px={globalPadding}
          mb={{ xs: 7, md: 9 }}
        >
          <Row cols={{ xs: 2, md: 3, lg: 4 }}>
            {survey?.items?.map((item) => (
              <VoteCard
                key={item.id}
                item={item}
                max={survey?.policy?.max_item_points}
                limited={getLimits(survey, votes)}
                disabled={disabled}
                onChange={handleVoteChange}
                userParticipatedPoints={userParticipatedPoints}
                value={item?.user_participated_item_points || votes?.[item.id]?.point}
              />
            ))}
          </Row>
        </VotesContainer>
      )}

      <Dialog
        width="xs"
        px={{ xs: 2, md: 5 }}
        open={showSummary}
        onClose={() => setShowSummary(false)}
      >
        <VoteSummary
          state={votes}
          onClear={handleClear}
          onConfirm={handleSubmit}
        />
      </Dialog>
    </div>
  );
}
